import { useState, useEffect } from 'react';
import { Typography, Box, Avatar, Button } from '@mui/material';
import './SupportDetail.css';
import Layout from '../../../../../components/Layout';
import AdminNavbar from '../../../../../components/Admin/AdminNavbar/AdminNavbar';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { formatDateFull } from '../../../../../utils/dateUtils';
import SupportDetailsSkeleton from '../../../../../components/SkeletonEffect/SupportDetailsSkeleton/SupportDetailsSkeleton';
import SupportResolveModal from '../../../../../components/Admin/Payments/SupportHelp/SupportResolveModal/SupportResolveModal';
import SupportRejectModal from '../../../../../components/Admin/Payments/SupportHelp/SupportRejectModal/SupportRejectModal';
import { toast } from 'react-hot-toast';

const formatText = text => {
  if (!text) return text;
  return text
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const StatusColor = {
  pending: { color: '#E0AD14', bgcolor: '#FFFCF2', value: 'Pending' },
  resolved: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Resolved' },
  rejected: { color: '#E14949', bgcolor: '#FFF2F2', value: 'Rejected' },
  default: { color: '#949DA6', bgcolor: '#F8F9FA', value: 'Unknown' },
};

const UserRoleColor = {
  teacher: { color: '#BC8D00', bgcolor: '#FFF3CE', value: 'Instructor' },
  student: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Learner' },
  learner: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Learner' },
};

const SupportDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const passedTicket = location.state?.ticket;

  const [ticket, setTicket] = useState(passedTicket || null);
  const [loading, setLoading] = useState(!passedTicket);
  const [error, setError] = useState(null);
  const [openResolveModal, setResolveOpen] = useState(false);
  const [openRejectModal, setRejectOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rbacMe, setRbacMe] = useState(null);

  const hasPermission = permission => {
    if (!rbacMe || !Array.isArray(rbacMe.permissions)) return false;
    return rbacMe.permissions.includes(permission);
  };

  const canManageSupportTickets = () => {
    return hasPermission('manage_support_tickets') || (rbacMe?.roles || []).includes('super_admin');
  };

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let cachedData = null;
        try {
          const cached = sessionStorage.getItem('rbac_admin_me');
          if (cached) {
            cachedData = JSON.parse(cached);
            setRbacMe(cachedData);
          }
        } catch (_) {}

        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);

        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!rbacMe) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTicket = async () => {
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get(`/admin/support-and-help/${id}`);
      setTicket(data || null);
    } catch (err) {
      setError('Failed to load support ticket.');
      setTicket(null);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    setActionLoading(true);
    try {
      await axiosInstance.patch(`/admin/support-and-help/${id}`, {
        status: 'resolved',
      });
      toast.success('Support ticket resolved successfully');
      fetchTicket();
      setResolveOpen(false);
    } catch (error) {
      console.error('Error resolving ticket:', error);
      toast.error(error.response?.data?.message || 'Failed to resolve ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async reason => {
    setActionLoading(true);
    try {
      await axiosInstance.patch(`/admin/support-and-help/${id}`, {
        status: 'rejected',
        rejection_reason: reason,
      });
      toast.success('Support ticket rejected successfully');
      fetchTicket();
      setRejectOpen(false);
    } catch (error) {
      console.error('Error rejecting ticket:', error);
      toast.error(error.response?.data?.message || 'Failed to reject ticket');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    if (passedTicket) setTicket(passedTicket);
    fetchTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <Layout title="Klass Online | Support & Help">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          {loading ? (
            <SupportDetailsSkeleton />
          ) : error ? (
            <Box className="flex flex-col p-[1rem] gap-[10px]">
              <Typography>{error}</Typography>
              <button onClick={fetchTicket} className="supportdetail-back-button">
                Retry
              </button>
            </Box>
          ) : !ticket ? (
            <Box className="flex flex-col p-[1rem] gap-[10px]">
              <Typography>Ticket not found</Typography>
              <button
                onClick={() => navigate('/admin/payments/disputes', { state: { activeTab: 1 } })}
                className="supportdetail-back-button"
              >
                ← Back to Support List
              </button>
            </Box>
          ) : (
            <Box className="flex flex-col p-[1rem] gap-[10px]">
              <Box className="flex flex-col gap-[5px]">
                <Box className="flex items-center gap-1">
                  <Typography
                    className="pointer-text"
                    onClick={() =>
                      navigate('/admin/payments/disputes', { state: { activeTab: 1 } })
                    }
                  >
                    Payments {'>'} Support & Help {'>'}
                  </Typography>
                  <Typography className="view-text">View</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    marginBottom: '8px',
                  }}
                >
                  <div className="dispute-header">
                    <Typography className="dispute-header-title">Ticket ID #{ticket.id}</Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        borderRadius: '20px',
                        border: `1px solid ${
                          StatusColor[ticket.status?.toLowerCase()]
                            ? StatusColor[ticket.status.toLowerCase()].color
                            : StatusColor.default.color
                        }`,
                        backgroundColor: StatusColor[ticket.status?.toLowerCase()]
                          ? StatusColor[ticket.status.toLowerCase()].bgcolor
                          : StatusColor.default.bgcolor,
                        color: StatusColor[ticket.status?.toLowerCase()]
                          ? StatusColor[ticket.status.toLowerCase()].color
                          : StatusColor.default.color,
                        padding: '2px 8px',
                        maxWidth: 'fit-content',
                      }}
                    >
                      <Box
                        sx={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: StatusColor[ticket.status?.toLowerCase()]
                            ? StatusColor[ticket.status.toLowerCase()].color
                            : StatusColor.default.color,
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: 'Poppins',
                          fontSize: '12px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: 'normal',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {StatusColor[ticket.status?.toLowerCase()]
                          ? StatusColor[ticket.status.toLowerCase()].value
                          : StatusColor.default.value}
                      </Typography>
                    </Box>
                  </div>
                  {ticket?.status?.toLowerCase() === 'pending' && canManageSupportTickets() && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setRejectOpen(true)}
                        disabled={actionLoading}
                        sx={{
                          minWidth: '110px',
                          borderRadius: '30px',
                          color: '#E14949',
                          borderColor: '#E14949',
                          textTransform: 'none',
                          fontFamily: 'Poppins',
                          fontWeight: 500,
                          fontSize: '14px',
                          padding: '8px 20px',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#C63535',
                            backgroundColor: 'rgba(225,73,73,0.04)',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => setResolveOpen(true)}
                        disabled={actionLoading}
                        sx={{
                          minWidth: '120px',
                          backgroundColor: '#14B334',
                          borderRadius: '30px',
                          color: '#fff',
                          textTransform: 'none',
                          fontFamily: 'Poppins',
                          fontWeight: 500,
                          fontSize: '14px',
                          padding: '8px 24px',
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: '#0F9428',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        {actionLoading ? 'Processing...' : 'Resolve'}
                      </Button>
                    </Box>
                  )}
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {ticket.created_at ? formatDateFull(ticket.created_at) : 'N/A'}
                </Typography>
              </Box>

              <div className="details-section">
                <h3 className="details-title">Ticket Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Created Date</span>
                    <span className="info-value">
                      {ticket.created_at ? formatDateFull(ticket.created_at) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Last Updated</span>
                    <span className="info-value">
                      {ticket.updated_at ? formatDateFull(ticket.updated_at) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Issue Category</span>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {(ticket.issue_category || []).map((category, index) => (
                        <span
                          key={index}
                          style={{
                            background: '#f5f5f5',
                            color: '#666',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            textTransform: 'capitalize',
                          }}
                        >
                          {formatText(category)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ padding: '0 20px 20px 20px' }}>
                  <span className="info-label" style={{ display: 'block', marginBottom: '8px' }}>
                    Issue Description
                  </span>
                  <div
                    style={{
                      background: '#f9f9f9',
                      padding: '16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      color: '#333',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {ticket.description || 'No description provided'}
                  </div>
                </div>
                {(ticket.issue_category || []).includes('bank_account_update') && (
                  <div style={{ padding: '0 20px 20px 20px' }}>
                    <span className="info-label" style={{ display: 'block', marginBottom: '8px' }}>
                      Update Bank Details (Requested)
                    </span>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Account Holder Name</span>
                        <span className="info-value">{ticket.bank_full_name || '---'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">IBAN Number</span>
                        <span className="info-value">{ticket.bank_iban_no || '---'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Bank Name</span>
                        <span className="info-value">{ticket.bank_name || '---'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Bank Address</span>
                        <span className="info-value">{ticket.bank_address || '---'}</span>
                      </div>
                    </div>
                  </div>
                )}
                {ticket.file_url && (
                  <div style={{ padding: '0 20px 20px 20px' }}>
                    <span className="info-label" style={{ display: 'block', marginBottom: '8px' }}>
                      {(ticket.issue_category || []).includes('bank_account_update')
                        ? 'Bank Statement (Last 3 weeks)'
                        : 'Attachment'}
                    </span>
                    <div className="supportdetail-attachment-section">
                      <div className="supportdetail-attachment-item">
                        <img
                          src={ticket.file_url}
                          alt="Support attachment"
                          className="supportdetail-attachment-image"
                          onError={e => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <a
                          href={ticket.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="supportdetail-attachment-link"
                        >
                          {(ticket.issue_category || []).includes('bank_account_update')
                            ? 'View bank statement'
                            : 'View Full Size'}
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="details-section">
                <h3 className="details-title">Personal Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Name</span>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Avatar
                        src={
                          ticket.user?.profile_image && ticket.user.profile_image.startsWith('http')
                            ? ticket.user.profile_image
                            : undefined
                        }
                        sx={{ width: 25, height: 25 }}
                        onError={e => {
                          e.target.style.display = 'none';
                        }}
                      >
                        {ticket.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </Avatar>
                      <span className="info-value">{ticket.user?.name || 'Unknown User'}</span>
                    </Box>
                  </div>
                  <div className="info-item">
                    <span className="info-label">User Role</span>
                    <div
                      className="role-tag"
                      style={{
                        backgroundColor:
                          UserRoleColor[ticket.user?.user_type?.toLowerCase()]?.bgcolor ||
                          UserRoleColor.learner.bgcolor,
                        color:
                          UserRoleColor[ticket.user?.user_type?.toLowerCase()]?.color ||
                          UserRoleColor.learner.color,
                      }}
                    >
                      {UserRoleColor[ticket.user?.user_type?.toLowerCase()]?.value ||
                        UserRoleColor.learner.value}
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email</span>
                    <a
                      href={`mailto:${ticket.user?.email || ''}`}
                      style={{
                        color: '#4b465c',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                      }}
                    >
                      {ticket.user?.email || 'N/A'}
                    </a>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Phone</span>
                    <a
                      href={`tel:${ticket.user?.phone_no || ''}`}
                      style={{
                        color: '#4b465c',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                      }}
                    >
                      {ticket.user?.phone_no || 'N/A'}
                    </a>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Current Location</span>
                    <span className="info-value">
                      {(() => {
                        const city = ticket.user?.city;
                        const country = ticket.user?.country;
                        if (city && country && city !== country) {
                          return `${city}, ${country}`;
                        }
                        if (city) {
                          return city;
                        }
                        if (country) {
                          return country;
                        }
                        return '---';
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {ticket.status?.toLowerCase() === 'rejected' && ticket.rejection_reason && (
                <div
                  className="details-section"
                  style={{
                    marginBottom: '16px',
                    backgroundColor: '#FFF2F2',
                    borderRadius: '12px',
                    border: '1px solid #E14949',
                  }}
                >
                  <h3 className="details-title">Rejection Details</h3>
                  <div className="info-grid support-rejection-grid">
                    <div className="info-item">
                      <span className="info-label">Reason for Rejection</span>
                      <span className="info-value">{ticket.rejection_reason || '---'}</span>
                    </div>
                  </div>
                </div>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Modals */}
      {openResolveModal && (
        <SupportResolveModal setResolveOpen={setResolveOpen} onResolve={handleResolve} />
      )}
      {openRejectModal && (
        <SupportRejectModal setRejectOpen={setRejectOpen} onReject={handleReject} />
      )}
    </Layout>
  );
};

export default SupportDetail;
